import { faPython } from "@fortawesome/free-brands-svg-icons";
import {
  faCode,
  faFileArrowDown,
  faLayerGroup,
  faMap,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLingui } from "@lingui/react/macro";
import { Divider, Grid, List, Stack, Table, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import z from "zod";

import { DocHeading } from "@/components/docs/DocHeading.tsx";
import { DocsContent } from "@/components/docs/DocsContent";

export const Route = createFileRoute("/docs/platform/access")({
  component: RouteComponent,
  validateSearch: z
    .object({
      client: z.enum(["arcgis-pro", "qgis", "fme", "python"]),
      dataset: z.string(),
      type: z.enum(["api", "wms", "download"]),
    })
    .partial(),
});

function RouteComponent() {
  const {
    client: currentClient,
    dataset: currentDataset,
    type: currentType,
  } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { t } = useLingui();

  const rows: Array<[string, string]> = [
    [t`REALity Property Search`, t`Individual searches and inspection.`],
    [t`REALity Downloads`, t`Controlled data exports.`],
    [t`OpenMaps or another GIS application`, t`Map-based analysis.`],
    [t`Integration or API`, t`Repeated or automated processing.`],
  ];

  if (!currentClient || !currentDataset) {
    return (
      <DocsContent>
        <Title order={1} size="h2">{t`Integrate software with REALity`}</Title>
        <Text>
          {t`REALity supports both human users and other systems. Instead of each application setting up its own connection to Lantmäteriet, REALity provides a shared access layer with common authentication, authorisation, monitoring and cost allocation.`}
        </Text>

        <DocHeading order={2} size="h3">{t`Potential integrations`}</DocHeading>
        <List withPadding>
          <List.Item>OpenMaps</List.Item>
          <List.Item>EstateReader</List.Item>
          <List.Item>ArcGIS Pro</List.Item>
          <List.Item>QGIS</List.Item>
          <List.Item>ArcGIS Online</List.Item>
          <List.Item>{t`Internal Sweco applications.`}</List.Item>
          <List.Item>{t`Customer-specific solutions.`}</List.Item>
        </List>

        <DocHeading
          order={2}
          size="h3"
        >{t`Choosing the right interface`}</DocHeading>
        <Text>
          {t`The same underlying information may also be available through another Sweco application. The most suitable interface depends on the task:`}
        </Text>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t`Interface`}</Table.Th>
              <Table.Th>{t`Use for`}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(([name, usage]) => (
              <Table.Tr key={name}>
                <Table.Td>{name}</Table.Td>
                <Table.Td>{usage}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        <Stack>
          <Title order={2} size="h3">{t`Integration guide`}</Title>
          <Grid>
            {[
              ["arcgis-pro", "ArcGIS Pro"] as const,
              ["qgis", "QGIS"] as const,
              ["fme", "FME"] as const,
              ["python", "Python"] as const,
            ].map(([client, title]) => (
              <Grid.Col key={client} span={{ sm: 3, base: 6 }}>
                <Stack
                  bdrs="lg"
                  bg={`var(--mantine-color-green-${currentClient === client ? 6 : 0})`}
                  px="md"
                  py="xl"
                  align="center"
                  justify="center"
                  onClick={() =>
                    navigate({
                      search: {
                        client,
                        dataset: currentDataset,
                        type: currentType,
                      },
                    })
                  }
                >
                  <FontAwesomeIcon
                    icon={client === "python" ? faPython : faMap}
                    size="5x"
                    color={`var(--mantine-color-green-${currentClient === client ? 0 : 6})`}
                  />
                  <Title
                    order={5}
                    mt="lg"
                    c={`${currentClient === client ? "white" : "black"}`}
                  >
                    {title}
                  </Title>
                </Stack>
              </Grid.Col>
            ))}
          </Grid>

          <Divider />

          <Grid></Grid>

          <Divider />

          <Grid>
            {[
              ["api", "API"] as const,
              ["wms", "WMS"] as const,
              ["download", "Download"] as const,
            ].map(([type, title]) => (
              <Grid.Col key={type} span={{ sm: 4, base: 6 }}>
                <Stack
                  bdrs="lg"
                  bg={`var(--mantine-color-blue-${currentType === type ? 6 : 0})`}
                  px="md"
                  py="xl"
                  align="center"
                  justify="center"
                  onClick={() =>
                    navigate({
                      search: {
                        type,
                        dataset: currentDataset,
                        client: currentClient,
                      },
                    })
                  }
                >
                  <FontAwesomeIcon
                    icon={
                      type === "api"
                        ? faCode
                        : type === "wms"
                          ? faLayerGroup
                          : faFileArrowDown
                    }
                    size="5x"
                    color={`var(--mantine-color-blue-${currentType === type ? 0 : 6})`}
                  />
                  <Title
                    order={5}
                    mt="lg"
                    c={`${currentType === type ? "white" : "black"}`}
                  >
                    {title}
                  </Title>
                </Stack>
              </Grid.Col>
            ))}
          </Grid>
        </Stack>
      </DocsContent>
    );
  } else {
    return null;
  }
}
